'use client';

import { motion } from 'motion/react';
import {
  Target,
  Zap,
  Shield,
  Globe,
  Heart,
  Sparkles,
  MessageSquare,
} from 'lucide-react';
import { useAppRouter } from '~/hooks';

export default function AboutUsPage() {
  const router = useAppRouter();

  const values = [
    {
      icon: Target,
      title: 'Precision & Accuracy',
      description:
        'We leverage cutting-edge AI technology to deliver the most accurate and reliable data extraction results in the industry.',
    },
    {
      icon: Zap,
      title: 'Speed & Efficiency',
      description:
        'Our platform processes thousands of data points in seconds, helping businesses save time and accelerate their growth.',
    },
    {
      icon: Shield,
      title: 'Security & Privacy',
      description:
        'We prioritize data security with enterprise-grade encryption and comply with all major data protection regulations.',
    },
    {
      icon: Heart,
      title: 'Customer Success',
      description:
        'Your success is our success. We provide dedicated support and resources to ensure you achieve your business goals.',
    },
  ];

  const stats = [
    { number: '50K+', label: 'Active Users' },
    { number: '10M+', label: 'Leads Extracted' },
    { number: '99.9%', label: 'Uptime' },
    { number: '24/7', label: 'Support' },
  ];

  const team = [
    {
      name: 'Sarah Chen',
      role: 'CEO & Founder',
      description:
        'Former Google engineer with 15+ years in AI and data science.',
    },
    {
      name: 'Michael Torres',
      role: 'CTO',
      description:
        'Ex-Amazon architect specializing in scalable data infrastructure.',
    },
    {
      name: 'Emily Rodriguez',
      role: 'Head of Product',
      description:
        'Product leader with expertise in B2B SaaS and marketing automation.',
    },
    {
      name: 'David Kim',
      role: 'Head of AI',
      description: 'PhD in Machine Learning, pioneering extraction algorithms.',
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="px-4 py-20 md:py-32 relative overflow-hidden bg-linear-to-br from-background-secondary via-secondary to-accent dark:from-[#1e293b] dark:via-[#312e81] dark:to-[#3730a3]">
        {/* Animated Background Elements */}
        <motion.div
          className="absolute top-20 right-10 w-72 h-72 bg-linear-to-br from-primary/40 to-[#8b5cf6]/30 rounded-full opacity-60 blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            x: [0, 30, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute bottom-20 left-10 w-96 h-96 bg-linear-to-tr from-[#8b5cf6]/20 to-[#ec4899]/20 rounded-full opacity-60 blur-3xl"
          animate={{
            scale: [1, 1.3, 1],
            x: [0, -40, 0],
            y: [0, 40, 0],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />

        {/* linear Mesh Overlay */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iY3VycmVudENvbG9yIiBzdHJva2Utb3BhY2l0eT0iMC4xIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30 text-primary dark:opacity-20"></div>

        {/* Floating Sketched Elements */}
        {/* Star sketch top-left */}
        <motion.svg
          className="absolute top-10 left-10 w-16 h-16 text-primary/40"
          animate={{
            y: [0, -20, 0],
            rotate: [0, 15, 0],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <path d="M50 10 L55 40 L85 45 L60 65 L70 95 L50 80 L30 95 L40 65 L15 45 L45 40 Z" />
          <circle cx="50" cy="50" r="3" fill="currentColor" />
        </motion.svg>

        {/* Lightning bolt sketch top-right */}
        <motion.svg
          className="absolute top-20 right-20 w-20 h-20 text-primary/50"
          animate={{
            y: [0, 15, 0],
            x: [0, -10, 0],
          }}
          transition={{
            duration: 3.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.5,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M55 10 L25 45 L45 48 L35 90 L75 45 L55 43 Z" />
        </motion.svg>

        {/* Checkmark circle sketch bottom-left */}
        <motion.svg
          className="absolute bottom-16 left-16 w-24 h-24 text-primary/30"
          animate={{
            scale: [1, 1.1, 1],
            rotate: [0, -10, 0],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <circle cx="50" cy="50" r="40" strokeDasharray="5,5" />
          <path d="M30 50 L45 65 L70 35" strokeWidth="3" />
        </motion.svg>

        {/* Arrow sketch bottom-right */}
        <motion.svg
          className="absolute bottom-20 right-16 w-28 h-28 text-primary/40"
          animate={{
            x: [0, 10, 0],
            y: [0, -10, 0],
            rotate: [0, 5, 0],
          }}
          transition={{
            duration: 4.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1.5,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10 50 Q50 10 90 50" />
          <path d="M70 30 L90 50 L70 70" />
          <circle cx="15" cy="48" r="3" fill="currentColor" />
        </motion.svg>

        {/* Sparkle sketch middle-left */}
        <motion.svg
          className="absolute top-1/2 left-1/4 w-12 h-12 text-primary/60 hidden lg:block"
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, 180, 360],
            opacity: [0.6, 1, 0.6],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          <path d="M50 10 L50 90 M10 50 L90 50" />
          <path d="M20 20 L80 80 M80 20 L20 80" />
        </motion.svg>

        {/* Scribble circle middle-right */}
        <motion.svg
          className="absolute top-1/3 right-1/4 w-20 h-20 text-primary/35 hidden lg:block"
          animate={{
            rotate: [0, 360],
            scale: [1, 1.15, 1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <circle cx="50" cy="50" r="30" strokeDasharray="3,3" />
          <circle cx="50" cy="50" r="20" strokeDasharray="2,4" />
          <circle cx="50" cy="50" r="10" strokeDasharray="1,2" />
        </motion.svg>

        {/* Dotted heart sketch */}
        <motion.svg
          className="absolute bottom-1/3 left-1/3 w-16 h-16 text-primary/45 hidden md:block"
          animate={{
            y: [0, -15, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.8,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          <path
            d="M50 85 C50 85, 20 60, 20 40 C20 25, 30 15, 40 15 C45 15, 50 20, 50 20 C50 20, 55 15, 60 15 C70 15, 80 25, 80 40 C80 60, 50 85, 50 85 Z"
            strokeDasharray="4,4"
          />
        </motion.svg>

        {/* Plus signs */}
        <motion.svg
          className="absolute top-1/4 right-1/3 w-10 h-10 text-primary/50 hidden lg:block"
          animate={{
            rotate: [0, 90, 0],
            opacity: [0.5, 1, 0.5],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1.2,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        >
          <path d="M50 20 L50 80 M20 50 L80 50" />
        </motion.svg>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="mb-6 text-foreground font-bold">About Leadbunker</h1>
            <p className="text-foreground-secondary max-w-2xl mx-auto mb-8">
              We&apos;re revolutionizing how businesses discover and connect
              with potential customers. Leadbunker is the AI-driven B2B solution
              that empowers companies to extract valuable contact information
              from the web efficiently, ethically, and at scale.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 px-4 bg-card/30 relative overflow-hidden">
        {/* Arrow sketch top-right */}
        <motion.svg
          className="absolute top-10 right-10 w-16 h-16 text-primary/40 hidden lg:block"
          animate={{
            x: [0, 20, 0],
            y: [0, -10, 0],
            rotate: [0, 10, 0],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1.5,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10 50 Q50 10 90 50" />
          <path d="M70 30 L90 50 L70 70" />
          <circle cx="15" cy="48" r="3" fill="currentColor" />
        </motion.svg>

        {/* Sparkle sketch bottom-left */}
        <motion.svg
          className="absolute bottom-10 left-10 w-12 h-12 text-primary/50 hidden lg:block"
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, 180, 360],
            opacity: [0.5, 1, 0.5],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          <path d="M50 10 L50 90 M10 50 L90 50" />
          <path d="M20 20 L80 80 M80 20 L20 80" />
        </motion.svg>

        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            <div>
              <h2 className="mb-4 text-foreground font-bold">Our Mission</h2>
              <p className="text-foreground-secondary mb-4">
                To democratize access to high-quality business intelligence and
                make lead generation accessible to companies of all sizes. We
                believe every business deserves the tools to grow and succeed in
                the digital age.
              </p>
              <p className="text-foreground-secondary">
                Through innovative AI technology and a commitment to ethical
                data practices, we&apos;re building the future of B2B marketing
                and sales intelligence.
              </p>
            </div>
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                {stats.map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-card border border-card-border rounded-xl p-6 text-center"
                  >
                    <div className="text-primary mb-2">{stat.number}</div>
                    <div className="text-foreground-secondary">
                      {stat.label}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 px-4 relative overflow-hidden bg-linear-to-br from-background-secondary via-secondary to-accent dark:from-[#1e293b] dark:via-[#312e81] dark:to-[#3730a3]">
        {/* Scribble circle top-left */}
        <motion.svg
          className="absolute top-20 left-5 w-20 h-20 text-primary/35 hidden lg:block"
          animate={{
            rotate: [0, 360],
            scale: [1, 1.15, 1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <circle cx="50" cy="50" r="30" strokeDasharray="3,3" />
          <circle cx="50" cy="50" r="20" strokeDasharray="2,4" />
          <circle cx="50" cy="50" r="10" strokeDasharray="1,2" />
        </motion.svg>

        {/* Plus sign middle-right */}
        <motion.svg
          className="absolute top-1/2 right-5 w-10 h-10 text-primary/50 hidden lg:block"
          animate={{
            rotate: [0, 90, 0],
            opacity: [0.5, 1, 0.5],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1.2,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        >
          <path d="M50 20 L50 80 M20 50 L80 50" />
        </motion.svg>

        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="mb-4 text-foreground font-bold">Our Core Values</h2>
            <p className="text-foreground-secondary max-w-2xl mx-auto">
              These principles guide everything we do and shape the culture of
              our company.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-card border border-card-border rounded-xl p-6 hover:shadow-lg transition-shadow"
              >
                <value.icon className="w-12 h-12 text-primary mb-4" />
                <h3 className="mb-3 text-foreground">{value.title}</h3>
                <p className="text-foreground-secondary">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 px-4 bg-card/30 relative overflow-hidden">
        {/* Heart sketch bottom-left */}
        <motion.svg
          className="absolute bottom-1/3 left-1/3 w-16 h-16 text-primary/45 hidden md:block"
          animate={{
            y: [0, -15, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.8,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          <path
            d="M50 85 C50 85, 20 60, 20 40 C20 25, 30 15, 40 15 C45 15, 50 20, 50 20 C50 20, 55 15, 60 15 C70 15, 80 25, 80 40 C80 60, 50 85, 50 85 Z"
            strokeDasharray="4,4"
          />
        </motion.svg>

        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="mb-4 text-foreground font-bold">
              Meet Our Leadership
            </h2>
            <p className="text-foreground-secondary max-w-2xl mx-auto">
              Our team brings together decades of experience in AI, data
              engineering, and B2B technology.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-card border border-card-border rounded-xl p-6 text-center"
              >
                <div className="w-20 h-20 bg-linear-to-br from-primary to-[#8b5cf6] rounded-full mx-auto mb-4 flex items-center justify-center text-primary-foreground">
                  {member.name
                    .split(' ')
                    .map((n) => n[0])
                    .join('')}
                </div>
                <h3 className="mb-1 text-foreground">{member.name}</h3>
                <div className="text-primary mb-3">{member.role}</div>
                <p className="text-foreground-secondary">
                  {member.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 px-4 relative overflow-hidden">
        {/* Curved arrow sketch top-right */}
        <motion.svg
          className="absolute top-10 right-20 w-24 h-24 text-primary/40 hidden lg:block"
          animate={{
            y: [0, -30, 0],
            x: [0, 15, 0],
            rotate: [0, 12, 0],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20 80 Q50 20 80 80" strokeDasharray="5,5" />
          <path d="M60 65 L80 80 L65 85" />
        </motion.svg>

        {/* Star sketch bottom-left */}
        <motion.svg
          className="absolute bottom-1/4 left-1/4 w-14 h-14 text-primary/30 hidden md:block"
          animate={{
            rotate: [0, 360],
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path
            d="M50 10 L60 40 L90 40 L65 60 L75 90 L50 70 L25 90 L35 60 L10 40 L40 40 Z"
            strokeDasharray="3,3"
          />
        </motion.svg>

        <div className="max-w-4xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-linear-to-br from-primary/10 to-[#8b5cf6]/10 border border-primary/20 rounded-2xl p-8"
          >
            <Globe className="w-12 h-12 text-primary mb-4" />
            <h2 className="mb-4 text-foreground font-bold">Our Story</h2>
            <div className="space-y-4 text-foreground-secondary">
              <p>
                Leadbunker was born from a simple frustration: gathering quality
                leads was too slow, too expensive, and too inefficient for
                modern businesses. Our founders, having worked at leading tech
                companies, saw an opportunity to leverage AI and machine
                learning to transform the lead generation landscape.
              </p>
              <p>
                Since our launch in 2022, we&apos;ve helped thousands of
                businesses streamline their prospecting processes, saving
                millions of hours and generating billions in pipeline value.
                What started as a small team in San Francisco has grown into a
                global operation serving customers in over 50 countries.
              </p>
              <p>
                Today, Leadbunker processes millions of data points daily,
                continuously improving through machine learning and staying at
                the forefront of AI-driven business intelligence. We&apos;re not
                just building a tool—we&apos;re building the future of how
                businesses connect.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-linear-to-br from-primary via-[#8b5cf6] to-[#ec4899] relative overflow-hidden">
        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="mb-4 text-white font-bold">Ready to Get Started?</h2>
            <p className="text-white/95 mb-8 max-w-2xl mx-auto">
              Join thousands of businesses using Leadbunker to accelerate their
              growth.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <motion.button
                onClick={() => router.push('/auth/signup')}
                className="px-8 py-4 bg-white text-primary rounded-lg hover:bg-white/95 transition-all shadow-xl hover:shadow-2xl flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <Sparkles className="w-5 h-5" />
                Start Free Trial
              </motion.button>
              <motion.button
                onClick={() => router.push('/contact')}
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white border border-white/20 rounded-lg hover:bg-white/20 transition-colors flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <MessageSquare className="w-5 h-5" />
                Contact Sales
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
